/**
 * Single active browser session per account (same profile):
 * - New login writes a fresh authSessionId; other tabs detect mismatch with their tab-bound id and sign out locally.
 * - Other devices/browsers require the API to invalidate old JWTs (e.g. session version / jti); this file only handles tabs.
 */

export const LS_AUTH_SESSION_ID = 'authSessionId';
const SS_BOUND_SESSION = 'authBoundSessionId';
const SS_SUPERSEDED = 'authSuperseded';

function newSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Call after a successful login in this tab (token already in localStorage). */
export function beginNewAuthSession() {
  try {
    const sid = newSessionId();
    localStorage.setItem(LS_AUTH_SESSION_ID, sid);
    sessionStorage.setItem(SS_BOUND_SESSION, sid);
    sessionStorage.removeItem(SS_SUPERSEDED);
    return sid;
  } catch (_) {
    return null;
  }
}

export function clearAllAuthStorage() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(LS_AUTH_SESSION_ID);
    localStorage.removeItem('guestSessionExpiry');
    localStorage.removeItem('guestUsedAt');
    localStorage.removeItem('guestEverUsed');
    localStorage.removeItem('guestCooldownUntil');
    localStorage.removeItem('tv_manual_logout_at');
  } catch (_) {}
  try {
    sessionStorage.removeItem(SS_BOUND_SESSION);
    sessionStorage.removeItem(SS_SUPERSEDED);
    sessionStorage.removeItem('tv_toast_session_superseded');
  } catch (_) {}
}

export function isAuthSuperseded() {
  try {
    return sessionStorage.getItem(SS_SUPERSEDED) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Align this tab with localStorage. Call on startup and after cross-tab storage events.
 * @returns {boolean} true if this tab must drop auth (another tab logged in with a newer session).
 */
export function syncTabAuthSessionWithStorage() {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      sessionStorage.removeItem(SS_BOUND_SESSION);
      sessionStorage.removeItem(SS_SUPERSEDED);
      return false;
    }

    let sid = localStorage.getItem(LS_AUTH_SESSION_ID);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(LS_AUTH_SESSION_ID, sid);
    }

    const bound = sessionStorage.getItem(SS_BOUND_SESSION);
    if (!bound) {
      sessionStorage.setItem(SS_BOUND_SESSION, sid);
      sessionStorage.removeItem(SS_SUPERSEDED);
      return false;
    }

    if (bound !== sid) {
      sessionStorage.setItem(SS_SUPERSEDED, '1');
      return true;
    }

    sessionStorage.removeItem(SS_SUPERSEDED);
    return false;
  } catch (_) {
    return false;
  }
}

/** Redux initial token: returns token from localStorage if valid; cleans up superseded state on explicit reload. */
export function readInitialAuthTokenForStore() {
  if (typeof window === 'undefined') return null;
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // When the user is reloading or actively visiting the site in this tab with a valid token,
    // re-bind to the active session id so reload NEVER signs out the user.
    let sid = localStorage.getItem(LS_AUTH_SESSION_ID);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(LS_AUTH_SESSION_ID, sid);
    }
    sessionStorage.setItem(SS_BOUND_SESSION, sid);
    sessionStorage.removeItem(SS_SUPERSEDED);
    return token;
  } catch (_) {
    return null;
  }
}

export function subscribeRemoteAuthStorage(onChange) {
  const handler = (e) => {
    if (!e.storageArea || e.storageArea !== localStorage) return;
    if (e.key != null && e.key !== LS_AUTH_SESSION_ID && e.key !== 'token') return;
    onChange();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
